import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Dealer } from '../../dealers.model';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { DealersService } from '../../dealers.service';
import { finalize, Subscription } from 'rxjs';
import { DeleteConfirmationModalComponent } from '../../../common/components/delete-confirmation-modal/delete-confirmation-modal.component';
import { Action, FirmType, FirmTypeLabel, PaginatedResponse } from '../../../common/models/common.model';
import { buildFilterQuery } from '../../../utils';
import { EmployeesService } from '../../../employees/employees.service';
import { Designation, EmployeeBasicInfo, SalesOfficersRole } from '../../../employees/employees.model';
import { DesignationService } from '../../../employees/designation.service';

@Component({
  selector: 'app-dealers',
  standalone: false,
  templateUrl: './dealers.component.html',
  styleUrl: './dealers.component.scss',
})
export class DealersComponent implements OnInit, OnDestroy {
  @ViewChild('addNewDealerContent') addNewDealerContent!: TemplateRef<any>;
  @ViewChild('exportDealersModal') exportDealersModal!: TemplateRef<any>;

  protected isDealersDataLoading: boolean = false;
  protected dealersList: Dealer[] = [];
  protected modalRef?: NgbModalRef;
  protected dealerToBeEdit: Dealer | null = null;
  protected hasNextPage = false;
  protected hasPreviousPage = false;
  protected totalCount: number = 0;
  protected searchTerm: string = '';

  protected currentPage: number = 1;

  /** Sr. No. sort direction. The API orders by dealer_number, not the UUID pk. */
  protected sortDirection: 'asc' | 'desc' = 'asc';

  protected salesOfficers: EmployeeBasicInfo[] = [];
  protected designations: Designation[] = [];
  protected selectedSalesOfficerId: string | null = null;
  protected selectedDesignationId: string | null = null;

  protected isExportingDealerList: boolean = false;
  protected dealerAreas: string[] = [];
  protected salesEmployees: EmployeeBasicInfo[] = [];
  protected exportFilters = {
    area: null,
    employee: null,
    firm_type: null,
  };

  private _dealersSubscription!: Subscription;
  private _exportSub?: Subscription;
  private _subs = new Subscription();
  private searchDebounceTimer!: any;

  protected readonly Action = Action;

  constructor(
    private _modalService: NgbModal,
    private _dealersService: DealersService,
    private _employeesService: EmployeesService,
    private _designationService: DesignationService,
    private _router: Router
  ) {}

  ngOnInit(): void {
    this._getDealersList();
    this._loadExportFilterData();
    this._loadListFilterData();
  }

  protected viewDealerDetails(dealerId: string): void {
    this._router.navigate(['/dealers', dealerId]);
  }

  // Protected methods

  protected onAddNewDealer(): void {
    this.dealerToBeEdit = null;

    const modalOptions: NgbModalOptions = {
      backdrop: 'static',
      size: 'lg',
    };

    this._openModal(this.addNewDealerContent, modalOptions);
  }

  protected editDealer(dealer: Dealer): void {
    this.dealerToBeEdit = dealer;

    const modalOptions: NgbModalOptions = {
      backdrop: 'static',
      size: 'lg',
    };

    this._openModal(this.addNewDealerContent, modalOptions);
  }

  protected onClickDeleteDealer(dealerId: string): void {
    const modalRef = this._modalService.open(DeleteConfirmationModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    // Optionally pass inputs
    modalRef.componentInstance.title = 'Delete Dealer';
    modalRef.componentInstance.message = 'Are you sure you want to delete this dealer?';
    modalRef.componentInstance.warning = 'This action cannot be undone.';

    modalRef.result.then(() => {
      this._dealersSubscription = this._dealersService.delete(dealerId).subscribe({
        next: () => {
          this.dealersList = this.dealersList.filter((dealer: Dealer) => {
            return dealer.id !== dealerId;
          });

          this.currentPage = 1;
          this._getDealersList();
        },
      });
    });
  }

  protected onSaveDealersInfo(dealersInfo: Dealer): void {
    this.currentPage = 1;
    this._getDealersList();
    this.modalRef?.dismiss();
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this._getDealersList();
  }

  protected onExportDealerList(): void {
    this.exportFilters = { area: null, employee: null, firm_type: null };
    this._modalService.open(this.exportDealersModal, { backdrop: 'static', size: 'md' });
  }

  protected onConfirmExport(modal: any): void {
    this.isExportingDealerList = true;

    const parts: string[] = [];
    if (this.exportFilters.area) parts.push(this.exportFilters.area);
    if (this.exportFilters.firm_type) parts.push(this.exportFilters.firm_type);
    const filename = parts.length ? `dealers_${parts.join('_')}.xlsx` : 'dealers.xlsx';

    this._exportSub = this._dealersService
      .exportDealerList({
        search: this.searchTerm || undefined,
        area: this.exportFilters.area || undefined,
        employee: this.exportFilters.employee || undefined,
        firm_type: this.exportFilters.firm_type || undefined,
      })
      .pipe(finalize(() => (this.isExportingDealerList = false)))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
          modal.dismiss();
        },
      });
  }

  protected onToggleDealerNumberSort(): void {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.currentPage = 1;
    this._getDealersList();
  }

  protected onListFilterChange(): void {
    this.currentPage = 1;
    this._getDealersList();
  }

  protected onSearchKeyUp(): void {
    // Clear any existing timer
    clearTimeout(this.searchDebounceTimer);

    // Set a new debounce timer (500 ms)
    this.searchDebounceTimer = setTimeout(() => {
      this.currentPage = 1;
      this._getDealersList();
    }, 500);
  }

  // Private methods

  private _getDealersList(): void {
    this.isDealersDataLoading = true;

    const q = buildFilterQuery({
      page: this.currentPage,
      search: this.searchTerm,
      // Matches dealers where the employee/designation sits in any of the SO, ASM or RSM slots.
      sales_officer: this.selectedSalesOfficerId,
      designation: this.selectedDesignationId,
      ordering: this.sortDirection === 'asc' ? 'dealer_number' : '-dealer_number',
    });

    this._dealersSubscription = this._dealersService
      .list(q)
      .pipe(
        finalize(() => {
          this.isDealersDataLoading = false;
        })
      )
      .subscribe({
        next: (dealersListResponse: PaginatedResponse<Dealer>) => {
          this.dealersList = dealersListResponse.results || [];
          this.totalCount = dealersListResponse.count;
          this.hasNextPage = !!dealersListResponse.next;
          this.hasPreviousPage = !!dealersListResponse.previous;
        },
      });
  }

  private _loadExportFilterData(): void {
    this._subs.add(
      this._dealersService.getDealerAreas().subscribe({
        next: areas => (this.dealerAreas = areas),
      })
    );
    this._subs.add(
      this._employeesService.lighter_list({ team: 'sales' }).subscribe({
        next: employees => (this.salesEmployees = employees),
      })
    );
  }

  private _loadListFilterData(): void {
    // Both endpoints return bare arrays, not a paginated envelope.
    this._subs.add(
      this._employeesService.getSalesUsersList(SalesOfficersRole.SALES_OFFICERS).subscribe({
        next: (salesOfficers: EmployeeBasicInfo[]) => (this.salesOfficers = salesOfficers),
      })
    );
    this._subs.add(
      this._designationService.list().subscribe({
        next: (designations: Designation[]) => (this.designations = designations),
      })
    );
  }

  private _openModal(content: TemplateRef<any>, options: NgbModalOptions): void {
    this.modalRef = this._modalService.open(content, options);
  }

  ngOnDestroy(): void {
    this.modalRef?.dismiss();
    this._dealersSubscription?.unsubscribe();
    this._exportSub?.unsubscribe();
    this._subs.unsubscribe();
  }

  protected readonly FirmTypeLabel = FirmTypeLabel;
  protected readonly FirmType = FirmType;
  protected readonly FirmTypeEntries = Object.entries(FirmTypeLabel) as [FirmType, string][];
}
